import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';
import { pageSEO } from '../data/seoData';
import SEOHead from '../components/SEOHead';

const galleryImages = [
  { id: 1, src: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800&q=80', category: 'painting', title: 'Interior Wall Painting' },
  { id: 2, src: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80', category: 'painting', title: 'Living Room Fresh Paint' },
  { id: 3, src: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80', category: 'remodeling', title: 'Kitchen Remodel - Plano' },
  { id: 4, src: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=800&q=80', category: 'tile', title: 'Bathroom Tile Installation' },
  { id: 5, src: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80', category: 'carpentry', title: 'Custom Crown Molding' },
  { id: 6, src: 'https://images.unsplash.com/photo-1600489000022-c2086d79f9d4?w=800&q=80', category: 'remodeling', title: 'Modern Kitchen Cabinets' },
  { id: 7, src: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&q=80', category: 'tile', title: 'Kitchen Backsplash Tile' },
  { id: 8, src: 'https://images.unsplash.com/photo-1591825729269-caeb344f6df2?w=800&q=80', category: 'deck', title: 'Custom Deck Construction' },
  { id: 9, src: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&q=80', category: 'siding', title: 'Exterior Home with Siding' },
  { id: 10, src: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80', category: 'drywall', title: 'Drywall Installation' },
  { id: 11, src: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80', category: 'siding', title: 'Modern Home Exterior' },
  { id: 12, src: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=800&q=80', category: 'deck', title: 'Outdoor Living Space' },
];

const categories = [
  { id: 'all', name: 'All Projects' },
  { id: 'painting', name: 'Painting' },
  { id: 'remodeling', name: 'Remodeling' },
  { id: 'carpentry', name: 'Carpentry' },
  { id: 'tile', name: 'Tile' },
  { id: 'deck', name: 'Decks' },
  { id: 'siding', name: 'Siding' },
  { id: 'drywall', name: 'Drywall' },
];

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredImages = activeCategory === 'all' ? galleryImages : galleryImages.filter(img => img.category === activeCategory);

  return (
    <div>
      <SEOHead 
        title={pageSEO.gallery.title}
        description={pageSEO.gallery.description}
        canonical="/gallery"
      />
      {/* Hero */}
      <section className="relative py-24 bg-neutral-900">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url('https://images.pexels.com/photos/6474471/pexels-photo-6474471.jpeg?auto=compress&cs=tinysrgb&w=1260')` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Work Gallery</h1>
          <p className="text-xl text-neutral-300 max-w-3xl mx-auto">Browse our portfolio of completed projects across the Dallas-Fort Worth area.</p>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((cat) => (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-colors ${activeCategory === cat.id ? 'bg-amber-500 text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}>
                {cat.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredImages.map((image) => (
              <div key={image.id} className="group relative overflow-hidden rounded-xl shadow-md cursor-pointer" onClick={() => setSelectedImage(image)}>
                <img src={image.src} alt={image.title} className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 via-neutral-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="text-white font-semibold text-lg">{image.title}</h3>
                  <span className="text-amber-400 text-sm capitalize">{image.category}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <button className="absolute top-4 right-4 text-white hover:text-amber-400" onClick={() => setSelectedImage(null)}>
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage.src} alt={selectedImage.title} className="w-full h-auto rounded-lg" />
            <div className="text-center mt-4">
              <h3 className="text-white font-semibold text-xl">{selectedImage.title}</h3>
              <span className="text-amber-400 capitalize">{selectedImage.category}</span>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to See Your Home Transformed?</h2>
          <p className="text-white/90 text-lg mb-8">Your project could be in our gallery next. Get a free estimate today!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-8 py-6 text-lg">
                Get Free Estimate <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-600 font-semibold px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />{companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
